import type * as React from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import * as iconError from '~/assets/icons/icon_error.svg';
import { colors, fontWeight } from '~/styles/constants';

interface TypographyProps {
  color?: string;
  fontSize?: string;
  lineHeight?: string;
}

const typographyBaseCss = css`
  font-family: inherit, serif;
  font-style: normal;
  color: ${colors.gray1000};
  padding: 0;
  margin: 0;
`;

const overrideCss = (props: TypographyProps) => {
  const { color, fontSize, lineHeight } = props;
  return css`
    ${color ? `color: ${color};` : ''}
    ${fontSize ? `font-size: ${fontSize};` : ''}
    ${lineHeight ? `line-height: ${lineHeight};` : ''}
  `;
};

export const Heading4 = styled.h4<TypographyProps>`
  ${typographyBaseCss}
  font-weight: ${fontWeight.normal};
  font-size: 16px;
  line-height: 24px;
  ${(props) => overrideCss(props)}
`;

export const Body = styled.p<TypographyProps>`
  ${typographyBaseCss}
  font-weight: ${fontWeight.normal};
  font-size: 14px;
  line-height: 24px;
  ${(props) => overrideCss(props)}
`;

const Caption = styled.p<TypographyProps>`
  ${typographyBaseCss}
  font-weight: ${fontWeight.normal};
  font-size: 12px;
  line-height: 20px;
  ${(props) => overrideCss(props)}
`;

export const Paragraph = styled.div`
  margin-top: -8px;

  & > * {
    margin-top: 8px;
  }
`;

const ErrorTextContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ErrorIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 12px;
`;

interface ComponentProps {
  caption?: boolean;
  children: React.ReactNode;
}

export const ErrorMessage = ({ caption = false, children }: ComponentProps) => {
  const TextElement = caption ? Caption : Body;
  return (
    <ErrorTextContainer>
      <ErrorIcon src={iconError} />
      <TextElement color={colors.ruby700}>{children}</TextElement>
    </ErrorTextContainer>
  );
};
